import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Badge, LoadingSkeleton, Modal, Button, PasswordInput } from '../components/ui';
import { ArrowLeftIcon, UserGroupIcon, AcademicCapIcon, UsersIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';

function StatBox({ label, value, icon: Icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
      <Icon style={{ width: 24, height: 24, color: 'var(--color-primary)' }} />
      <div>
        <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{value ?? 0}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{label}</div>
      </div>
    </div>
  );
}

const EMPTY_ADMIN_FORM = { full_name: '', username: '', email: '', password: '' };
const EMPTY_PRINCIPAL_FORM = { full_name: '', username: '', email: '', password: '' };
const EMPTY_EMAIL_CONFIG_FORM = {
  from_name: '', from_email: '', smtp_server: '', smtp_port: '', smtp_use_tls: true, smtp_username: '', smtp_password: '',
};

function TenantDetail({ token }) {
  const { id } = useParams();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [presets, setPresets] = useState([]);
  const [applyingId, setApplyingId] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState('');
  const logoInputRef = useRef(null);
  const [featureModules, setFeatureModules] = useState([]);
  const [togglingFeature, setTogglingFeature] = useState(null);
  const [attendanceModes, setAttendanceModes] = useState([]);
  const [applyingAttendanceMode, setApplyingAttendanceMode] = useState(null);

  const [emailStatus, setEmailStatus] = useState(null);
  const [editingEmailConfig, setEditingEmailConfig] = useState(false);
  const [emailConfigForm, setEmailConfigForm] = useState(EMPTY_EMAIL_CONFIG_FORM);
  const [emailConfigError, setEmailConfigError] = useState('');
  const [emailConfigSaving, setEmailConfigSaving] = useState(false);

  const [administrators, setAdministrators] = useState([]);
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [adminForm, setAdminForm] = useState(EMPTY_ADMIN_FORM);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_ADMIN_FORM);
  const [adminError, setAdminError] = useState('');
  const [adminSaving, setAdminSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [principals, setPrincipals] = useState([]);
  const [addingPrincipal, setAddingPrincipal] = useState(false);
  const [principalForm, setPrincipalForm] = useState(EMPTY_PRINCIPAL_FORM);
  const [editingPrincipal, setEditingPrincipal] = useState(null);
  const [editPrincipalForm, setEditPrincipalForm] = useState(EMPTY_PRINCIPAL_FORM);
  const [principalError, setPrincipalError] = useState('');
  const [principalSaving, setPrincipalSaving] = useState(false);
  const [togglingPrincipalId, setTogglingPrincipalId] = useState(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/superadmin/schools/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        setSchool(await response.json());
      } else {
        setError('Failed to load school detail');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  const fetchPresets = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/superadmin/theme-presets', { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) setPresets(await response.json());
    } catch {
      // Swapped presets aren't essential to loading the page — a page-level
      // error alert would be noisy if only this one call fails.
    }
  }, [token]);

  const fetchAdministrators = useCallback(async () => {
    try {
      const response = await fetch(`/api/v1/superadmin/schools/${id}/administrators`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) setAdministrators(await response.json());
    } catch {
      // Same rationale as fetchPresets — non-essential to the page loading.
    }
  }, [token, id]);

  const fetchPrincipals = useCallback(async () => {
    try {
      const response = await fetch(`/api/v1/superadmin/schools/${id}/principals`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) setPrincipals(await response.json());
    } catch {
      // Same rationale as fetchPresets — non-essential to the page loading.
    }
  }, [token, id]);

  const fetchFeatureModules = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/superadmin/feature-modules', { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) setFeatureModules(await response.json());
    } catch {
      // Same rationale as fetchPresets — non-essential to the page loading.
    }
  }, [token]);

  const fetchAttendanceModes = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/superadmin/attendance-modes', { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) setAttendanceModes(await response.json());
    } catch {
      // Same rationale as fetchPresets — non-essential to the page loading.
    }
  }, [token]);

  const fetchEmailStatus = useCallback(async () => {
    try {
      const response = await fetch(`/api/v1/superadmin/schools/${id}/email-config`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) setEmailStatus(await response.json());
    } catch {
      // Same rationale as fetchPresets — non-essential to the page loading.
    }
  }, [token, id]);

  useEffect(() => { fetchDetail(); fetchPresets(); fetchAdministrators(); fetchPrincipals(); fetchFeatureModules(); fetchAttendanceModes(); fetchEmailStatus(); }, [fetchDetail, fetchPresets, fetchAdministrators, fetchPrincipals, fetchFeatureModules, fetchAttendanceModes, fetchEmailStatus]);

  const handleSelectPreset = async (presetId) => {
    setApplyingId(presetId);
    setError('');
    try {
      const response = await fetch(`/api/v1/superadmin/schools/${id}/theme`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ preset: presetId }),
      });
      if (response.ok) {
        await fetchDetail();
      } else {
        setError(await safeErrorMessage(response, 'Failed to update theme'));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setApplyingId(null);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    setUploadingLogo(true);
    setLogoError('');
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const response = await fetch(`/api/v1/superadmin/schools/${id}/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        await fetchDetail();
      } else {
        setLogoError(await safeErrorMessage(response, 'Failed to upload logo'));
      }
    } catch {
      setLogoError('Network error. Please check your connection and try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    setUploadingLogo(true);
    setLogoError('');
    try {
      const response = await fetch(`/api/v1/superadmin/schools/${id}/logo`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        await fetchDetail();
      } else {
        setLogoError(await safeErrorMessage(response, 'Failed to remove logo'));
      }
    } catch {
      setLogoError('Network error. Please check your connection and try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleToggleFeature = async (key, enabled) => {
    setTogglingFeature(key);
    setError('');
    try {
      const response = await fetch(`/api/v1/superadmin/schools/${id}/features`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ feature: key, enabled }),
      });
      if (response.ok) {
        await fetchDetail();
      } else {
        setError(await safeErrorMessage(response, 'Failed to update feature'));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setTogglingFeature(null);
    }
  };

  const handleSelectAttendanceMode = async (mode) => {
    setApplyingAttendanceMode(mode);
    setError('');
    try {
      const response = await fetch(`/api/v1/superadmin/schools/${id}/attendance-mode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mode }),
      });
      if (response.ok) {
        await fetchDetail();
      } else {
        setError(await safeErrorMessage(response, 'Failed to update attendance mode'));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setApplyingAttendanceMode(null);
    }
  };

  const openEditEmailConfig = () => {
    setEmailConfigForm({
      from_name: emailStatus?.using_school_override ? (emailStatus.from_name || '') : '',
      from_email: emailStatus?.using_school_override ? (emailStatus.from_email || '') : '',
      smtp_server: emailStatus?.using_school_override ? (emailStatus.smtp_server || '') : '',
      smtp_port: emailStatus?.using_school_override ? (emailStatus.smtp_port || '') : '',
      smtp_use_tls: emailStatus?.smtp_use_tls ?? true,
      smtp_username: emailStatus?.using_school_override ? (emailStatus.smtp_username || '') : '',
      smtp_password: '',
    });
    setEmailConfigError('');
    setEditingEmailConfig(true);
  };

  const handleEmailConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEmailConfigForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEmailConfigSubmit = async (e) => {
    e.preventDefault();
    setEmailConfigSaving(true);
    setEmailConfigError('');
    try {
      const body = { ...emailConfigForm };
      if (!body.smtp_password) delete body.smtp_password;

      const response = await fetch(`/api/v1/superadmin/schools/${id}/email-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        setEditingEmailConfig(false);
        await fetchEmailStatus();
      } else {
        setEmailConfigError(await safeErrorMessage(response, 'Failed to update email configuration'));
      }
    } catch {
      setEmailConfigError('Network error. Please check your connection and try again.');
    } finally {
      setEmailConfigSaving(false);
    }
  };

  const handleClearEmailConfig = async () => {
    setEmailConfigSaving(true);
    setEmailConfigError('');
    try {
      const response = await fetch(`/api/v1/superadmin/schools/${id}/email-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clear: true }),
      });
      if (response.ok) {
        await fetchEmailStatus();
      } else {
        setEmailConfigError(await safeErrorMessage(response, 'Failed to reset email configuration'));
      }
    } catch {
      setEmailConfigError('Network error. Please check your connection and try again.');
    } finally {
      setEmailConfigSaving(false);
    }
  };

  const openAddAdmin = () => {
    setAdminForm(EMPTY_ADMIN_FORM);
    setAdminError('');
    setAddingAdmin(true);
  };

  const handleAddAdminChange = (e) => setAdminForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleAddAdminSubmit = async (e) => {
    e.preventDefault();
    setAdminSaving(true);
    setAdminError('');
    try {
      const response = await fetch(`/api/v1/superadmin/schools/${id}/administrators`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(adminForm),
      });
      if (response.ok) {
        setAddingAdmin(false);
        await fetchAdministrators();
      } else {
        setAdminError(await safeErrorMessage(response, 'Failed to add administrator'));
      }
    } catch {
      setAdminError('Network error. Please check your connection and try again.');
    } finally {
      setAdminSaving(false);
    }
  };

  const openEditAdmin = (admin) => {
    setEditingAdmin(admin);
    setEditForm({ full_name: admin.full_name, username: admin.username, email: admin.email, password: '' });
    setAdminError('');
  };

  const handleEditAdminChange = (e) => setEditForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleEditAdminSubmit = async (e) => {
    e.preventDefault();
    setAdminSaving(true);
    setAdminError('');
    try {
      const body = { full_name: editForm.full_name, username: editForm.username, email: editForm.email };
      if (editForm.password) body.password = editForm.password;

      const response = await fetch(`/api/v1/superadmin/schools/${id}/administrators/${editingAdmin.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        setEditingAdmin(null);
        await fetchAdministrators();
      } else {
        setAdminError(await safeErrorMessage(response, 'Failed to update administrator'));
      }
    } catch {
      setAdminError('Network error. Please check your connection and try again.');
    } finally {
      setAdminSaving(false);
    }
  };

  const handleToggleActive = async (admin) => {
    setTogglingId(admin.id);
    setAdminError('');
    try {
      const response = admin.is_active
        ? await fetch(`/api/v1/superadmin/schools/${id}/administrators/${admin.id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          })
        : await fetch(`/api/v1/superadmin/schools/${id}/administrators/${admin.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ is_active: true }),
          });
      if (response.ok) {
        await fetchAdministrators();
      } else {
        setAdminError(await safeErrorMessage(response, 'Failed to update administrator status'));
      }
    } catch {
      setAdminError('Network error. Please check your connection and try again.');
    } finally {
      setTogglingId(null);
    }
  };

  const openAddPrincipal = () => {
    setPrincipalForm(EMPTY_PRINCIPAL_FORM);
    setPrincipalError('');
    setAddingPrincipal(true);
  };

  const handleAddPrincipalChange = (e) => setPrincipalForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleAddPrincipalSubmit = async (e) => {
    e.preventDefault();
    setPrincipalSaving(true);
    setPrincipalError('');
    try {
      const response = await fetch(`/api/v1/superadmin/schools/${id}/principals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(principalForm),
      });
      if (response.ok) {
        setAddingPrincipal(false);
        await fetchPrincipals();
      } else {
        setPrincipalError(await safeErrorMessage(response, 'Failed to add principal'));
      }
    } catch {
      setPrincipalError('Network error. Please check your connection and try again.');
    } finally {
      setPrincipalSaving(false);
    }
  };

  const openEditPrincipal = (principal) => {
    setEditingPrincipal(principal);
    setEditPrincipalForm({ full_name: principal.full_name, username: principal.username, email: principal.email, password: '' });
    setPrincipalError('');
  };

  const handleEditPrincipalChange = (e) => setEditPrincipalForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleEditPrincipalSubmit = async (e) => {
    e.preventDefault();
    setPrincipalSaving(true);
    setPrincipalError('');
    try {
      const body = { full_name: editPrincipalForm.full_name, username: editPrincipalForm.username, email: editPrincipalForm.email };
      if (editPrincipalForm.password) body.password = editPrincipalForm.password;

      const response = await fetch(`/api/v1/superadmin/schools/${id}/principals/${editingPrincipal.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        setEditingPrincipal(null);
        await fetchPrincipals();
      } else {
        setPrincipalError(await safeErrorMessage(response, 'Failed to update principal'));
      }
    } catch {
      setPrincipalError('Network error. Please check your connection and try again.');
    } finally {
      setPrincipalSaving(false);
    }
  };

  const handleTogglePrincipalActive = async (principal) => {
    setTogglingPrincipalId(principal.id);
    setPrincipalError('');
    try {
      const response = principal.is_active
        ? await fetch(`/api/v1/superadmin/schools/${id}/principals/${principal.id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          })
        : await fetch(`/api/v1/superadmin/schools/${id}/principals/${principal.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ is_active: true }),
          });
      if (response.ok) {
        await fetchPrincipals();
      } else {
        setPrincipalError(await safeErrorMessage(response, 'Failed to update principal status'));
      }
    } catch {
      setPrincipalError('Network error. Please check your connection and try again.');
    } finally {
      setTogglingPrincipalId(null);
    }
  };

  if (loading) return <LoadingSkeleton type="text" rows={8} />;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!school) return null;

  return (
    <div>
      <Link to="/superadmin/schools" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
        <ArrowLeftIcon style={{ width: 14, height: 14 }} />Back to Schools
      </Link>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-text)' }}>{school.name}</h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
            Code: <code>{school.slug}</code> · Created {school.created_at ? new Date(school.created_at).toLocaleDateString() : '—'}
          </p>
        </div>
        <Badge variant={school.status === 'active' ? 'success' : 'danger'}>{school.status}</Badge>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <StatBox label="Students" value={school.stats?.total_students} icon={UserGroupIcon} />
        <StatBox label="Teachers" value={school.stats?.total_teachers} icon={AcademicCapIcon} />
        <StatBox label="Users" value={school.stats?.total_users} icon={UsersIcon} />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--color-text)' }}>Administrators</h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
              Full-access accounts for this school. Only Super Admin can create or remove these.
            </p>
          </div>
          <Button variant="primary" size="sm" onClick={openAddAdmin}>
            <PlusIcon style={{ width: 14, height: 14 }} />
            Add Administrator
          </Button>
        </div>

        <Card>
          {adminError && <div className="alert alert-error">{adminError}</div>}
          {administrators.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>No administrator accounts yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {administrators.map((admin) => (
                <div key={admin.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{admin.full_name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{admin.username} · {admin.email}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Badge variant={admin.is_active ? 'success' : 'danger'}>{admin.is_active ? 'Active' : 'Inactive'}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => openEditAdmin(admin)}>
                      <PencilIcon style={{ width: 14, height: 14 }} />
                      Edit
                    </Button>
                    <Button
                      variant={admin.is_active ? 'danger' : 'secondary'}
                      size="sm"
                      onClick={() => handleToggleActive(admin)}
                      disabled={togglingId === admin.id}
                    >
                      {togglingId === admin.id ? 'Working…' : admin.is_active ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--color-text)' }}>Principals</h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
              Full-access accounts for this school. Only Super Admin can create or remove these.
            </p>
          </div>
          <Button variant="primary" size="sm" onClick={openAddPrincipal}>
            <PlusIcon style={{ width: 14, height: 14 }} />
            Add Principal
          </Button>
        </div>

        <Card>
          {principalError && <div className="alert alert-error">{principalError}</div>}
          {principals.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>No principal accounts yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {principals.map((principal) => (
                <div key={principal.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{principal.full_name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{principal.username} · {principal.email}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Badge variant={principal.is_active ? 'success' : 'danger'}>{principal.is_active ? 'Active' : 'Inactive'}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => openEditPrincipal(principal)}>
                      <PencilIcon style={{ width: 14, height: 14 }} />
                      Edit
                    </Button>
                    <Button
                      variant={principal.is_active ? 'danger' : 'secondary'}
                      size="sm"
                      onClick={() => handleTogglePrincipalActive(principal)}
                      disabled={togglingPrincipalId === principal.id}
                    >
                      {togglingPrincipalId === principal.id ? 'Working…' : principal.is_active ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card title="Theme">
        {error && <div className="alert alert-error">{error}</div>}

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem', paddingBottom: '1.25rem', borderBottom: '1px solid var(--color-border)' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 'var(--radius)', border: '1px solid var(--color-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: 'var(--color-bg)',
          }}>
            {school.theme?.logo_data_uri ? (
              <img src={school.theme.logo_data_uri} alt="School logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>No logo</span>
            )}
          </div>
          <div>
            {logoError && <div className="alert alert-error" style={{ marginBottom: '0.5rem' }}>{logoError}</div>}
            <Button variant="secondary" size="sm" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}>
              {uploadingLogo ? 'Working…' : school.theme?.logo_data_uri ? 'Replace logo' : 'Upload logo'}
            </Button>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleLogoUpload}
              disabled={uploadingLogo}
              style={{ display: 'none' }}
            />
            {school.theme?.logo_data_uri && (
              <Button variant="ghost" size="sm" onClick={handleRemoveLogo} disabled={uploadingLogo} style={{ marginLeft: '0.5rem' }}>
                Remove
              </Button>
            )}
            <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.375rem' }}>
              PNG, JPEG, or WebP, up to 512KB.
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem' }}>
          {presets.map((preset) => {
            const selected = school.theme?.preset === preset.id;
            const busy = applyingId === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => handleSelectPreset(preset.id)}
                disabled={applyingId !== null}
                style={{
                  display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem',
                  borderRadius: 'var(--radius)', cursor: applyingId !== null ? 'default' : 'pointer', textAlign: 'left',
                  border: selected ? `2px solid ${preset.colors['--color-primary']}` : '1px solid var(--color-border)',
                  background: selected ? preset.colors['--color-primary-50'] : 'var(--color-surface)',
                  opacity: applyingId !== null && !busy ? 0.6 : 1,
                }}
              >
                <div style={{ display: 'flex', gap: '0.375rem' }}>
                  <span style={{ width: 20, height: 20, borderRadius: '50%', background: preset.colors['--color-primary'] }} />
                  <span style={{ width: 20, height: 20, borderRadius: '50%', background: preset.colors['--color-secondary'] }} />
                </div>
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{preset.name}</span>
                {busy && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Applying…</span>}
                {selected && !busy && <Badge variant="primary">Current</Badge>}
              </button>
            );
          })}
        </div>
      </Card>

      <Card title="Features">
        {error && <div className="alert alert-error">{error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {featureModules.map((module) => {
            const enabled = school.features?.[module.id] !== false;
            const busy = togglingFeature === module.id;
            return (
              <div key={module.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{module.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Badge variant={enabled ? 'success' : 'default'}>{enabled ? 'Enabled' : 'Disabled'}</Badge>
                  <Button
                    variant={enabled ? 'danger' : 'secondary'}
                    size="sm"
                    onClick={() => handleToggleFeature(module.id, !enabled)}
                    disabled={togglingFeature !== null}
                  >
                    {busy ? 'Working…' : enabled ? 'Disable' : 'Enable'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card title="Attendance Mode">
        {error && <div className="alert alert-error">{error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {attendanceModes.map((mode) => {
            const selected = school.attendance_mode === mode.id;
            const busy = applyingAttendanceMode === mode.id;
            return (
              <div key={mode.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{mode.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {selected && !busy && <Badge variant="primary">Current</Badge>}
                  <Button
                    variant={selected ? 'secondary' : 'primary'}
                    size="sm"
                    onClick={() => handleSelectAttendanceMode(mode.id)}
                    disabled={applyingAttendanceMode !== null || selected}
                  >
                    {busy ? 'Applying…' : selected ? 'Selected' : 'Select'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card title="Email Configuration">
        {error && <div className="alert alert-error">{error}</div>}
        {emailConfigError && <div className="alert alert-error">{emailConfigError}</div>}
        {emailStatus && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                <div><strong>From:</strong> {emailStatus.from_name} &lt;{emailStatus.from_email}&gt;</div>
                <div><strong>SMTP server:</strong> {emailStatus.smtp_server}</div>
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                  <Badge variant={emailStatus.is_configured ? 'success' : 'danger'}>
                    {emailStatus.is_configured ? 'Configured' : 'Not configured'}
                  </Badge>
                  <Badge variant={emailStatus.using_school_override ? 'primary' : 'default'}>
                    {emailStatus.using_school_override ? 'School override' : 'Platform default'}
                  </Badge>
                  {emailStatus.using_school_override && (
                    <Badge variant={emailStatus.smtp_password_is_set ? 'success' : 'danger'}>
                      {emailStatus.smtp_password_is_set ? 'Password set' : 'Password not set'}
                    </Badge>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button variant="secondary" size="sm" onClick={openEditEmailConfig}>
                  <PencilIcon style={{ width: 14, height: 14 }} />
                  Edit
                </Button>
                {emailStatus.using_school_override && (
                  <Button variant="ghost" size="sm" onClick={handleClearEmailConfig} disabled={emailConfigSaving}>
                    Reset to platform default
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </Card>

      <Modal isOpen={editingEmailConfig} onClose={() => setEditingEmailConfig(false)} title="Email Configuration">
        <form onSubmit={handleEmailConfigSubmit}>
          {emailConfigError && <div className="alert alert-error">{emailConfigError}</div>}
          <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
            Leave a field blank to use the platform default for it.
          </p>
          <div className="form-group">
            <label>From Name</label>
            <input type="text" name="from_name" value={emailConfigForm.from_name} onChange={handleEmailConfigChange} />
          </div>
          <div className="form-group">
            <label>From Email</label>
            <input type="email" name="from_email" value={emailConfigForm.from_email} onChange={handleEmailConfigChange} />
          </div>
          <div className="form-group">
            <label>SMTP Server</label>
            <input type="text" name="smtp_server" value={emailConfigForm.smtp_server} onChange={handleEmailConfigChange} placeholder="e.g. smtp.gmail.com" />
          </div>
          <div className="form-group">
            <label>SMTP Port</label>
            <input type="number" name="smtp_port" value={emailConfigForm.smtp_port} onChange={handleEmailConfigChange} placeholder="e.g. 587" />
          </div>
          <div className="form-group">
            <label>
              <input type="checkbox" name="smtp_use_tls" checked={emailConfigForm.smtp_use_tls} onChange={handleEmailConfigChange} style={{ marginRight: '0.5rem' }} />
              Use TLS
            </label>
          </div>
          <div className="form-group">
            <label>SMTP Username</label>
            <input type="text" name="smtp_username" value={emailConfigForm.smtp_username} onChange={handleEmailConfigChange} />
          </div>
          <div className="form-group">
            <label>SMTP Password</label>
            <PasswordInput name="smtp_password" value={emailConfigForm.smtp_password} onChange={handleEmailConfigChange} placeholder="Leave blank to keep current password" />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem' }}>
            <Button type="submit" variant="primary" disabled={emailConfigSaving}>
              {emailConfigSaving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditingEmailConfig(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={addingAdmin} onClose={() => setAddingAdmin(false)} title="Add Administrator">
        <form onSubmit={handleAddAdminSubmit}>
          {adminError && <div className="alert alert-error">{adminError}</div>}
          <div className="form-group">
            <label>Full Name</label>
            <input type="text" name="full_name" value={adminForm.full_name} onChange={handleAddAdminChange} required />
          </div>
          <div className="form-group">
            <label>Username</label>
            <input type="text" name="username" value={adminForm.username} onChange={handleAddAdminChange} required />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" name="email" value={adminForm.email} onChange={handleAddAdminChange} required />
          </div>
          <div className="form-group">
            <label>Password</label>
            <PasswordInput name="password" value={adminForm.password} onChange={handleAddAdminChange} required minLength={6} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem' }}>
            <Button type="submit" variant="primary" disabled={adminSaving}>
              {adminSaving ? 'Adding...' : 'Add Administrator'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setAddingAdmin(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={Boolean(editingAdmin)} onClose={() => setEditingAdmin(null)} title={editingAdmin ? `Edit — ${editingAdmin.full_name}` : ''}>
        {editingAdmin && (
          <form onSubmit={handleEditAdminSubmit}>
            {adminError && <div className="alert alert-error">{adminError}</div>}
            <div className="form-group">
              <label>Full Name</label>
              <input type="text" name="full_name" value={editForm.full_name} onChange={handleEditAdminChange} required />
            </div>
            <div className="form-group">
              <label>Username</label>
              <input type="text" name="username" value={editForm.username} onChange={handleEditAdminChange} required />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" value={editForm.email} onChange={handleEditAdminChange} required />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <PasswordInput name="password" value={editForm.password} onChange={handleEditAdminChange} placeholder="Leave blank to keep current password" minLength={6} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem' }}>
              <Button type="submit" variant="primary" disabled={adminSaving}>
                {adminSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setEditingAdmin(null)}>Cancel</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={addingPrincipal} onClose={() => setAddingPrincipal(false)} title="Add Principal">
        <form onSubmit={handleAddPrincipalSubmit}>
          {principalError && <div className="alert alert-error">{principalError}</div>}
          <div className="form-group">
            <label>Full Name</label>
            <input type="text" name="full_name" value={principalForm.full_name} onChange={handleAddPrincipalChange} required />
          </div>
          <div className="form-group">
            <label>Username</label>
            <input type="text" name="username" value={principalForm.username} onChange={handleAddPrincipalChange} required />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" name="email" value={principalForm.email} onChange={handleAddPrincipalChange} required />
          </div>
          <div className="form-group">
            <label>Password</label>
            <PasswordInput name="password" value={principalForm.password} onChange={handleAddPrincipalChange} required minLength={6} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem' }}>
            <Button type="submit" variant="primary" disabled={principalSaving}>
              {principalSaving ? 'Adding...' : 'Add Principal'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setAddingPrincipal(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={Boolean(editingPrincipal)} onClose={() => setEditingPrincipal(null)} title={editingPrincipal ? `Edit — ${editingPrincipal.full_name}` : ''}>
        {editingPrincipal && (
          <form onSubmit={handleEditPrincipalSubmit}>
            {principalError && <div className="alert alert-error">{principalError}</div>}
            <div className="form-group">
              <label>Full Name</label>
              <input type="text" name="full_name" value={editPrincipalForm.full_name} onChange={handleEditPrincipalChange} required />
            </div>
            <div className="form-group">
              <label>Username</label>
              <input type="text" name="username" value={editPrincipalForm.username} onChange={handleEditPrincipalChange} required />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" value={editPrincipalForm.email} onChange={handleEditPrincipalChange} required />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <PasswordInput name="password" value={editPrincipalForm.password} onChange={handleEditPrincipalChange} placeholder="Leave blank to keep current password" minLength={6} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem' }}>
              <Button type="submit" variant="primary" disabled={principalSaving}>
                {principalSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setEditingPrincipal(null)}>Cancel</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

export default TenantDetail;
